import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const MIN_HEIGHT_PX = 240;

// This project's TS/lib config doesn't ship ResizeObserver's type declarations, even though the
// runtime API exists in every browser this app supports - declare just the shape actually used
// here (rather than casting through `any`) so it stays properly typed and feature-detected.
interface IResizeObserverLike {
    observe(target: Element): void;
    disconnect(): void;
}

interface IResizeObserverConstructor {
    new (callback: () => void): IResizeObserverLike;
}

declare const ResizeObserver: IResizeObserverConstructor | undefined;

// Sizes an element to fill exactly the space between its own top and the bottom of the viewport
// (minus offsetBottomPx, e.g. for a footer/margin rendered below it), measured directly via
// getBoundingClientRect rather than a hand-counted rem value - so it's correct regardless of how
// tall the navbar/search/button-row chrome above it actually renders, and stays correct if that
// chrome's height ever changes. Recalculates after every render of the component using this hook
// (tab switches, view swaps, spinners appearing/disappearing all move the element's top), on window
// resize, and via a ResizeObserver on <body> - that last one is what catches the chrome ABOVE this
// element changing size on its own (e.g. an async-loaded nav/search field) without this component
// itself re-rendering, which a render-only recalc would miss and leave sized against a stale top.
export function useFillRemainingHeight<T extends HTMLElement>(offsetBottomPx: number = 0) {
    const ref = useRef<T>(null);
    const [height, setHeight] = useState<number>(400);

    const recalc = useCallback(() => {
        if (ref.current) {
            const top = ref.current.getBoundingClientRect().top;
            setHeight(Math.max(window.innerHeight - top - offsetBottomPx, MIN_HEIGHT_PX));
        }
    }, [offsetBottomPx]);

    useLayoutEffect(() => {
        recalc();
    });

    useEffect(() => {
        window.addEventListener('resize', recalc);
        let observer: IResizeObserverLike | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => recalc());
            observer.observe(document.body);
        }
        return () => {
            window.removeEventListener('resize', recalc);
            observer?.disconnect();
        };
    }, [recalc]);

    return { ref, height };
}



// WEBPACK FOOTER //
// ./src/util/useFillRemainingHeight.ts